import React, { useState, useEffect, useCallback } from 'react';
import { api, API_BASE } from '../utils/api';
import { Copy, CheckCircle2, AlertTriangle, Send, Eye } from 'lucide-react';

// 日報未入力の LINE グループ通知の設定（ADR 0030）。本部の管理者だけが開ける。
// グループは公式アカウントのボットを招待すると自動で並ぶ。ここでは「どの会社の通知先か」を選ぶだけ。

const card = { background: 'var(--field)', border: '1px solid var(--rule-strong)', borderRadius: 16, padding: '16px 18px', marginBottom: 12 };
const lbl = { fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-mute)', marginBottom: 4, display: 'block' };
const code = { fontFamily: 'monospace', fontSize: 'var(--t-note)', color: 'var(--ink)', wordBreak: 'break-all' };
const smallBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 36, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--rule-strong)', background: 'transparent', color: 'var(--ink)', fontSize: 'var(--t-note)', cursor: 'pointer', whiteSpace: 'nowrap' };

// グループ名が取れなかったとき（ボットが名前を読めない等）でも見分けられるよう、ID の末尾を添える。
const groupLabel = (g) => g?.group_name || `名前なしのグループ（…${String(g?.group_id || '').slice(-6)}）`;

const STATUS_LABEL = { sent: '送信済み', failed: '失敗', skipped: '未送信（鍵なし）', pending: '送信中' };
const KIND_LABEL = { evening: '22時', morning: '翌10時', test: 'テスト' };

export default function AdminLineNotify() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null); // 処理中のボタン（group_id や 'preview'）

  const webhookUrl = `${API_BASE}/line/webhook`;

  // 失敗しても直前の表示は消さない（2026-09-27 の一覧が消えたバグと同じことをしない）
  const load = useCallback(async () => {
    setLoading(true);
    try { setStatus(await api.getLineStatus()); setError(null); }
    catch (e) { setError(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const copyWebhook = () => {
    navigator.clipboard?.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const assign = async (groupId, companyId) => {
    setBusy(groupId); setNotice(null);
    try {
      await api.setLineGroupCompany(groupId, companyId === '' ? null : Number(companyId));
      setNotice('通知先の会社を保存しました');
      await load();
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  const sendTest = async (group) => {
    if (!window.confirm(`「${group.group_name || 'このグループ'}」にテストメッセージを送ります。LINE の通数を使います。よろしいですか？`)) return;
    setBusy(group.group_id); setNotice(null);
    try { setNotice((await api.testLineGroup(group.group_id)).message); setError(null); }
    catch (e) { setError(e.message); }
    setBusy(null);
  };

  const runPreview = async () => {
    setBusy('preview');
    try { setPreview(await api.previewLineReminder()); setError(null); }
    catch (e) { setError(e.message); }
    setBusy(null);
  };

  const companies = status?.companies || [];
  const groups = status?.groups || [];
  const ready = status?.token_set && status?.secret_set;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <h1 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: 'var(--ink)' }}>LINE通知（日報未入力）</h1>
      <p style={{ margin: '0 0 20px', fontSize: 'var(--t-note)', color: 'var(--ink-soft)', lineHeight: 1.6 }}>
        その日に出勤した（時間別入力に数字を入れた・着座メモに関わった）のに日報が無い人を、
        会社ごとの LINE グループへ <b>22時（当日分）</b>と<b>翌朝10時（前日分）</b>に知らせます。
        未入力の人がいない会社には送りません。
      </p>

      {loading && !status ? (
        <div style={{ ...card, color: 'var(--ink-mute)', fontSize: 'var(--t-note)' }}>読み込み中...</div>
      ) : (
        <>
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 12 }}>セットアップ手順</div>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--t-note)', color: 'var(--ink)', lineHeight: 1.9 }}>
              <li>LINE Developers で公式アカウントの Messaging API チャネルを開く</li>
              <li>チャネルアクセストークンとチャネルシークレットを、開発担当に Worker の秘密情報として登録してもらう</li>
              <li><b>Webhook URL が空であることを確かめてから</b>、下の URL を設定し「Webhook の利用」をオンにする（別のシステムが使っていたら上書きしない）</li>
              <li>公式アカウントの設定で「グループ・複数人トークへの参加を許可する」をオンにする</li>
              <li>公式アカウントを各社の LINE グループに招待する → 下の一覧に並ぶ</li>
              <li>一覧で、それぞれのグループがどの会社の通知先かを選び、テスト送信で届くか確かめる</li>
            </ol>
          </div>

          <div style={card}>
            <label style={lbl}>Webhook URL（LINE Developers に貼る）</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ ...code, flex: 1 }}>{webhookUrl}</span>
              <button onClick={copyWebhook} style={{ ...smallBtn, color: copied ? 'var(--gain)' : 'var(--ink)' }}>
                {copied ? <><CheckCircle2 size={13} /> コピー済</> : <><Copy size={13} /> コピー</>}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginTop: 14 }}>
              <div>
                <label style={lbl}>チャネルアクセストークン</label>
                <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: status?.token_set ? 'var(--gain)' : 'var(--hold)' }}>{status?.token_set ? '設定済み' : '未設定'}</div>
              </div>
              <div>
                <label style={lbl}>チャネルシークレット</label>
                <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: status?.secret_set ? 'var(--gain)' : 'var(--hold)' }}>{status?.secret_set ? '設定済み' : '未設定'}</div>
              </div>
              <div>
                <label style={lbl}>今月の送信数（LINE）</label>
                <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink)' }}>
                  {status?.quota
                    ? `${status.quota.used ?? '-'} 通${status.quota.type === 'limited' ? ` / 上限 ${status.quota.limit} 通` : '（上限なし）'}`
                    : '取得できません'}
                </div>
              </div>
            </div>
            <p style={{ margin: '10px 0 0', fontSize: 'var(--t-note)', color: 'var(--ink-soft)', lineHeight: 1.6 }}>
              送信数は「送った回数 × グループで受け取った人数」で数えられ、公式アカウントの他の配信と合算です。
            </p>
          </div>

          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 12 }}>グループ（{groups.length}）</div>
            {groups.length === 0 ? (
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>
                まだありません。公式アカウントを LINE グループに招待すると、ここに並びます。
              </div>
            ) : groups.map(g => (
              <div key={g.group_id} style={{ padding: '12px 0', borderTop: '1px solid var(--rule)', opacity: g.left_at ? 0.55 : 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
                  {groupLabel(g)}
                  {g.left_at && <span style={{ marginLeft: 8, fontSize: 'var(--t-note)', color: 'var(--hold)' }}>退出済み（送りません）</span>}
                </div>
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginTop: 2 }}>参加: {g.joined_at}</div>
                {!g.left_at && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <select
                      className="form-input"
                      aria-label="通知先の会社"
                      value={g.company_id ?? ''}
                      disabled={busy === g.group_id}
                      onChange={e => assign(g.group_id, e.target.value)}
                      style={{ flex: '1 1 200px', minHeight: 44 }}
                    >
                      <option value="">会社を選ぶ（未選択なら送らない）</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button onClick={() => sendTest(g)} disabled={!ready || busy === g.group_id} style={{ ...smallBtn, minHeight: 44, opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'not-allowed' }}>
                      <Send size={13} /> テスト送信
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>今送ったら（送信はしません）</div>
              <button onClick={runPreview} disabled={busy === 'preview'} style={{ ...smallBtn, minHeight: 44 }}>
                <Eye size={13} /> 今日の分を確認
              </button>
            </div>
            {preview && (
              preview.reminders.length === 0 ? (
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>
                  {preview.missing.length === 0
                    ? `${preview.date}：出勤して日報が無い人はいません。`
                    : `${preview.date}：未入力が ${preview.missing.length} 名いますが、通知先のグループが割り当てられていない会社の人です。`}
                </div>
              ) : preview.reminders.map(r => (
                <div key={r.group_id} style={{ marginTop: 8 }}>
                  <label style={lbl}>{groupLabel(groups.find(g => g.group_id === r.group_id) || { group_id: r.group_id })} へ</label>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 'var(--t-note)', color: 'var(--ink)', background: 'var(--paper-sunk)', borderRadius: 8, padding: '8px 10px' }}>{r.text}</pre>
                </div>
              ))
            )}
          </div>

          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>最近の通知</div>
            {(status?.log || []).length === 0 ? (
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>まだ送っていません。</div>
            ) : status.log.map((l, i) => (
              <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', padding: '8px 0', borderTop: i ? '1px solid var(--rule)' : 'none', fontSize: 'var(--t-note)', color: 'var(--ink)' }}>
                <span>{l.target_date}（{KIND_LABEL[l.kind] || l.kind}）</span>
                <span style={{ color: 'var(--ink-soft)' }}>{groupLabel(l)}</span>
                <span>{l.recipients} 名</span>
                <span style={{ color: l.status === 'sent' ? 'var(--gain)' : 'var(--hold)', fontWeight: 700 }}>{STATUS_LABEL[l.status] || l.status}</span>
                {l.error && <span style={{ flexBasis: '100%', color: 'var(--hold)' }}>{l.error}</span>}
              </div>
            ))}
          </div>

          {notice && (
            <div style={{ ...card, border: '1px solid #1E5540', color: 'var(--gain)', fontSize: 'var(--t-note)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle2 size={15} /> {notice}
            </div>
          )}
          {error && (
            <div style={{ ...card, border: '1px solid #5C4718', color: 'var(--hold)', fontSize: 'var(--t-note)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={15} /> {error}
            </div>
          )}
        </>
      )}
    </div>
  );
}
